import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { CalendarIcon, ChevronDownIcon } from '@/components/icons'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameDay,
    isToday,
    isSameMonth,
    addMonths,
    subMonths,
    setMonth,
    setYear,
    format,
    parse,
    getMonth,
    getYear,
    startOfDay,
    isBefore,
} from 'date-fns'

const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const DatePickerComponent = ({ value, onChange, onCancel, autoSave = false, allowPastDates = false }) => {
    const [selectedDate, setSelectedDate] = useState(() => {
        // Parse date string as local date to avoid timezone issues
        return value ? parse(value, 'yyyy-MM-dd', new Date()) : new Date()
    })

    const [currentMonth, setCurrentMonth] = useState(() => {
        return value ? parse(value, 'yyyy-MM-dd', new Date()) : new Date()
    })

    // Generate calendar days using date-fns
    const daysInMonth = useMemo(() => {
        const start = startOfWeek(startOfMonth(currentMonth))
        const end = endOfWeek(endOfMonth(currentMonth))

        return eachDayOfInterval({ start, end })
    }, [currentMonth])

    const handlePrevMonth = useCallback(() => {
        setCurrentMonth((prev) => subMonths(prev, 1))
    }, [])

    const handleNextMonth = useCallback(() => {
        setCurrentMonth((prev) => addMonths(prev, 1))
    }, [])

    const handleMonthChange = useCallback((month) => {
        setCurrentMonth((prev) => setMonth(prev, month))
    }, [])

    const handleYearChange = useCallback((year) => {
        setCurrentMonth((prev) => setYear(prev, year))
    }, [])

    const handleDateSelect = useCallback(
        (date) => {
            // Prevent selection of past dates only if allowPastDates is false
            if (!allowPastDates) {
                const today = startOfDay(new Date())
                if (isBefore(date, today)) {
                    return
                }
            }

            setSelectedDate(date)

            // If autoSave is enabled, immediately save and close
            if (autoSave) {
                const formattedDate = format(date, 'yyyy-MM-dd')
                onChange(formattedDate)
            }
        },
        [autoSave, onChange, allowPastDates],
    )

    const handleSave = useCallback(() => {
        // Use date-fns format to avoid timezone issues
        onChange(format(selectedDate, 'yyyy-MM-dd'))
    }, [selectedDate, onChange])

    const currentYear = getYear(currentMonth)
    const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i)

    return (
        <div
            className="tw:absolute tw:top-full tw:left-0 tw:mt-1 tw:z-[60] tw:bg-[#071727] tw:rounded-[2px] tw:p-3 tw:w-[370px] tw:border tw:border-[#3B5168]"
            style={{ boxShadow: '0px 4px 24px 0px #05111D40' }}
        >
            {/* Header with month/year selectors */}
            <div className="tw:flex tw:items-center tw:justify-between tw:mb-6">
                <div className="tw:flex tw:items-center tw:gap-2">
                    <select
                        value={getMonth(currentMonth)}
                        onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                        className="tw:file:text-foreground tw:placeholder:text-muted-foreground tw:selection:bg-primary tw:selection:text-primary-foreground tw:dark:bg-background tw:border-[#3B5168] tw:flex tw:h-9 tw:min-w-0 tw:rounded-xs tw:border tw:bg-transparent tw:px-3 tw:py-1 tw:pr-2 tw:!text-sm tw:shadow-xs tw:transition-[color,box-shadow] tw:outline-none tw:text-white tw:focus-visible:border-blue-500 tw:focus-visible:border-[1px]"
                    >
                        {months.map((month, index) => (
                            <option key={index} value={index}>
                                {month}
                            </option>
                        ))}
                    </select>
                    <select
                        value={currentYear}
                        onChange={(e) => handleYearChange(parseInt(e.target.value))}
                        className="tw:file:text-foreground tw:placeholder:text-muted-foreground tw:selection:bg-primary tw:selection:text-primary-foreground tw:dark:bg-background tw:border-[#3B5168] tw:flex tw:h-9 tw:min-w-0 tw:rounded-xs tw:border tw:bg-transparent tw:px-3 tw:py-1 tw:pr-2 tw:!text-sm tw:shadow-xs tw:transition-[color,box-shadow] tw:outline-none tw:text-white tw:focus-visible:border-blue-500 tw:focus-visible:border-[1px]"
                    >
                        {years.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                    <button
                        onClick={handlePrevMonth}
                        className="tw:w-8 tw:h-8 tw:bg-[#2450BF33] tw:border tw:border-[#2450BF] tw:rounded tw:flex tw:items-center tw:justify-center tw:text-white hover:tw:bg-[#2450BF]/30 tw:transition-colors"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        onClick={handleNextMonth}
                        className="tw:w-8 tw:h-8 tw:bg-[#2450BF33] tw:border tw:border-[#2450BF] tw:rounded tw:flex tw:items-center tw:justify-center tw:text-white hover:tw:bg-[#2450BF]/30 tw:transition-colors"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>

            {/* Weekday headers */}
            <div className="tw:grid tw:grid-cols-7 tw:gap-1 tw:mb-2">
                {weekdays.map((day) => (
                    <div
                        key={day}
                        className="tw:h-8 tw:flex tw:items-center tw:justify-center tw:text-xs tw:text-gray-400 tw:font-medium"
                    >
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="tw:grid tw:grid-cols-7 tw:gap-1 tw:mb-6">
                {daysInMonth.map((date, index) => {
                    const isSelected = isSameDay(date, selectedDate)
                    const isCurrentMonthDay = isSameMonth(date, currentMonth)
                    const isTodayDate = isToday(date)
                    const today = startOfDay(new Date())
                    const isPastDate = isBefore(date, today)
                    const isDisabled = !allowPastDates && isPastDate

                    return (
                        <button
                            key={index}
                            onClick={() => handleDateSelect(date)}
                            disabled={isDisabled}
                            className={cn(
                                'tw:h-8 tw:flex tw:items-center tw:justify-center tw:text-sm tw:rounded tw:transition-colors',
                                isSelected && !isDisabled && 'tw:bg-[#3871FF] tw:text-white',
                                !isSelected &&
                                    isCurrentMonthDay &&
                                    !isDisabled &&
                                    'tw:text-white hover:tw:bg-[#3B5168]',
                                !isSelected && !isCurrentMonthDay && !isDisabled && 'tw:text-gray-500',
                                isTodayDate && !isSelected && !isDisabled && 'tw:font-bold',
                                isDisabled && 'tw:text-gray-600 tw:cursor-not-allowed tw:opacity-50',
                                // When past dates are allowed, style past dates normally
                                allowPastDates &&
                                    isPastDate &&
                                    !isSelected &&
                                    isCurrentMonthDay &&
                                    'tw:text-white hover:tw:bg-[#3B5168]',
                                allowPastDates && isPastDate && !isSelected && !isCurrentMonthDay && 'tw:text-gray-500',
                            )}
                        >
                            {format(date, 'd')}
                        </button>
                    )
                })}
            </div>

            {/* Action buttons - only show if not autoSave */}
            {!autoSave && (
                <div className="tw:flex tw:gap-2">
                    <Button onClick={onCancel} variant="secondary" className="tw:flex-1 tw:h-7">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} className="tw:flex-1 tw:h-7">
                        Save
                    </Button>
                </div>
            )}
        </div>
    )
}

const DatePicker = ({
    value,
    onChange,
    placeholder = 'Select date',
    className,
    hideIcon = false,
    autoSave = false,
    allowPastDates = false,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef(null)
    const [displayValue, setDisplayValue] = useState(() => {
        if (value) {
            // Parse and format using date-fns to avoid timezone issues
            const parsedDate = parse(value, 'yyyy-MM-dd', new Date())
            return format(parsedDate, 'dd/MM/yyyy')
        }
        return placeholder
    })

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const handleOpenPicker = useCallback(() => {
        setIsOpen(true)
    }, [])

    const handleClosePicker = useCallback(() => {
        setIsOpen(false)
    }, [])

    const handleDateChange = useCallback(
        (newDate) => {
            // Format display value using date-fns to avoid timezone issues
            setDisplayValue(format(new Date(newDate), 'dd/MM/yyyy'))
            onChange?.(newDate)
            setIsOpen(false)
        },
        [onChange],
    )

    return (
        <div ref={containerRef} className="tw:relative">
            <button
                onClick={handleOpenPicker}
                className={cn(
                    'tw:file:text-foreground tw:placeholder:text-muted-foreground tw:selection:bg-primary tw:selection:text-primary-foreground tw:dark:bg-background tw:border-[#3B5168] tw:flex tw:h-9 tw:w-full tw:min-w-0 tw:rounded-xs tw:border tw:bg-transparent tw:px-3 tw:py-1 tw:!text-sm tw:shadow-xs tw:transition-[color,box-shadow] tw:outline-none tw:file:inline-flex tw:file:h-7 tw:file:border-0 tw:file:bg-transparent tw:file:text-sm tw:file:font-medium tw:disabled:pointer-events-none tw:disabled:cursor-not-allowed tw:disabled:opacity-50 tw:md:text-sm tw:focus-visible:border-blue-500 tw:focus-visible:border-[1px] tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:ring-destructive/40 tw:aria-invalid:border-destructive tw:items-center tw:justify-between tw:text-white tw:cursor-pointer',
                    className,
                )}
                {...props}
            >
                <div className="tw:flex tw:items-center tw:gap-2">
                    {!hideIcon && <CalendarIcon />}
                    <span className="tw:text-white">{displayValue}</span>
                </div>
                <ChevronDownIcon />
            </button>

            {isOpen && (
                <DatePickerComponent
                    value={value}
                    onChange={handleDateChange}
                    onCancel={handleClosePicker}
                    autoSave={autoSave}
                    allowPastDates={allowPastDates}
                />
            )}
        </div>
    )
}

export { DatePicker }
